import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api, errMsg, fieldErrs, statusOf } from "@/lib/api";
import { BILL_CATEGORIES } from "@/lib/constants";
import { Field } from "@/components/common";

const blank = { vendor: "", amount: "", due_date: "", category: "Utilities", notes: "" };

function validate(f) {
  const e = {};
  if (!f.vendor.trim()) e.vendor = "Add who the bill is from";
  if (f.amount === "" || f.amount == null) e.amount = "Add the amount";
  else if (!(Number(f.amount) > 0)) e.amount = "Amount must be more than $0";
  if (!f.due_date) e.due_date = "Add the due date";
  return e;
}

export function BillForm({ hubId, initial, billId, fileId, onSaved, onCancel }) {
  const [f, setF] = useState({ ...blank, ...Object.fromEntries(Object.entries(initial || {}).filter(([, v]) => v != null)) });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [dup, setDup] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => setDup(false), [f.vendor, f.amount, f.due_date]);

  const set = (k) => (e) => {
    setF({ ...f, [k]: e.target.value });
    setErrors({ ...errors, [k]: "" });
  };

  const save = async (force = false) => {
    const errs = validate(f);
    setErrors(errs);
    setError("");
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const body = { ...f, file_id: fileId || null, force };
      const { data } = billId ? await api.put(`/organizations/${hubId}/bills/${billId}`, body) : await api.post(`/organizations/${hubId}/bills`, body);
      onSaved(data);
    } catch (e) {
      if (statusOf(e) === 409) setDup(true);
      else if (fieldErrs(e)) setErrors(fieldErrs(e));
      else setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); save(false); }} className="space-y-4" noValidate data-testid="bill-form">
      <Field label="Vendor" htmlFor="bill-vendor" error={errors.vendor} errorTestid="bill-vendor-error">
        <input id="bill-vendor" className="q-input" placeholder="e.g. Con Edison" value={f.vendor} onChange={set("vendor")} aria-invalid={!!errors.vendor} data-testid="bill-vendor-input" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Amount (USD)" htmlFor="bill-amount" error={errors.amount} errorTestid="bill-amount-error">
          <input id="bill-amount" type="number" inputMode="decimal" step="0.01" min="0" className="q-input" placeholder="0.00" value={f.amount} onChange={set("amount")} aria-invalid={!!errors.amount} data-testid="bill-amount-input" />
        </Field>
        <Field label="Due date" htmlFor="bill-due" error={errors.due_date} errorTestid="bill-due-error">
          <input id="bill-due" type="date" className="q-input" value={f.due_date} onChange={set("due_date")} aria-invalid={!!errors.due_date} data-testid="bill-due-input" />
        </Field>
      </div>
      <Field label="Category" htmlFor="bill-cat" error={errors.category}>
        <select id="bill-cat" className="q-input" value={f.category} onChange={set("category")} data-testid="bill-category-select">
          {BILL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Notes (optional)" htmlFor="bill-notes">
        <input id="bill-notes" className="q-input" value={f.notes} onChange={set("notes")} data-testid="bill-notes-input" />
      </Field>
      {error && <p className="q-error" data-testid="bill-form-error">{error}</p>}
      {dup ? (
        <div className="rounded-xl border px-4 py-3" style={{ borderColor: "rgba(var(--st-yellow),0.4)", background: "rgba(var(--st-yellow),0.08)" }} data-testid="bill-duplicate-warning">
          <p className="flex items-center gap-2 text-sm font-semibold text-yellow-200"><AlertTriangle className="h-4 w-4" /> Looks like you already added this bill</p>
          <div className="mt-3 flex gap-2">
            <button type="button" className="q-btn q-btn-primary py-2" disabled={busy} onClick={() => save(true)} data-testid="bill-save-anyway-btn">Save anyway</button>
            <button type="button" className="q-btn q-btn-secondary py-2" onClick={onCancel} data-testid="bill-duplicate-cancel-btn">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="q-btn q-btn-ghost" onClick={onCancel} data-testid="bill-cancel-btn">Cancel</button>
          <button className="q-btn q-btn-primary" disabled={busy} data-testid="bill-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      )}
    </form>
  );
}
